/** @jsx jsx */

import { useRef, useEffect } from 'react';
import { css, jsx } from '@emotion/core';
import { colors, getButtonFocus } from 'styles/variables';
import getStore from 'services/getStore';
import getTopics from 'services/apiHelpers/getTopics';
import setCurrentTopic from 'services/apiHelpers/setCurrentTopic';
import isIE from 'services/isIE';
import { VisuallyHidden } from 'components/UI/VisuallyHidden';
import pubSub from 'services/pubSub';
import onScrollEnds from 'services/onScrollEnds';
import getSectionInView from 'services/getSectionInView';
import { getTopicsNotHiddenFromNextAndBackNavigation } from 'services/getTopicsNotHiddenFromNextAndBackNavigation';

const store = getStore();
const { lGrey2 } = colors;

let firstSection;
let scrolling;

const Arrow = ({ color }) => {
  return (
    <svg width="9px" height="15px" viewBox="0 0 9 15">
      <g stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
        <g
          transform="translate(-1206.000000, -566.000000)"
          stroke={color}
          strokeWidth="2"
        >
          <polyline
            transform="translate(1210.000000, 573.500000) rotate(-90.000000) translate(-1210.000000, -573.500000)"
            points="1216.5 570.5 1210 576.5 1203.5 570.5"
          />
        </g>
      </g>
    </svg>
  );
};

const navToPrevTopic = () => {
  let { currentTopic } = store.getState();
  const topics = getTopics();

  const topicsNotHiddenFromNextAndBackNavigation = getTopicsNotHiddenFromNextAndBackNavigation(
    topics
  );

  const prevTopic =
    topicsNotHiddenFromNextAndBackNavigation[
      topicsNotHiddenFromNextAndBackNavigation.findIndex(
        t => t.id === currentTopic
      ) - 1
    ];

  if (!prevTopic) return;

  setCurrentTopic(prevTopic.id);

  requestAnimationFrame(() => window.scrollTo(0, document.body.scrollHeight));
};

const BackBtn = ({ currentTopic, topics, color, outline = false }) => {
  if (!currentTopic) {
    currentTopic = topics[0]?.id;
  }

  const backBtn = useRef<HTMLAnchorElement | null>(null);

  useEffect(() => {
    if (window.dev()) {
      return;
    }

    // if we're not in the first topic. return;
    if (topics.findIndex(t => t.id === currentTopic.id) !== 0) {
      return;
    }

    if (!firstSection) {
      firstSection = document.querySelector('section');
      if (!firstSection) {
        return;
      }
    }

    const onScroll = () => {
      clearTimeout(scrolling);

      scrolling = setTimeout(() => {
        const { top } = firstSection.getBoundingClientRect();

        if (top < 0) {
          backBtn.current!.style.opacity = '1';
          backBtn.current!.style.pointerEvents = 'auto';
          backBtn.current!.setAttribute('aria-hidden', 'false');
        } else {
          backBtn.current!.style.opacity = '0.1';
          backBtn.current!.style.pointerEvents = 'none';
          backBtn.current!.setAttribute('aria-hidden', 'true');
        }

        // console.log(backBtn.current!.getAttribute('aria-hidden'));
      }, 100);
    };

    onScroll();
    window.addEventListener('scroll', onScroll);

    return () => {
      firstSection = null;
      window.removeEventListener('scroll', onScroll);
    };
  }, [currentTopic]);

  useEffect(() => {
    pubSub.subscribe('HILITE_CHAMELEON_BACK_BUTTON', ({ toggle }) => {
      backBtn.current!.style.boxShadow = toggle
        ? `0 0 0 2px #fff, 0 0 0 3px ${color}`
        : '';
    });
  }, []);

  const onClick = () => {
    // are we in an assessment ?
    const { blocks } = store.getState();

    const interaction = blocks[window['sectionInView'].id];
    const interactionIsAnAssessmentQuestion =
      interaction?.meta?.subType === 'AssessmentQuestion';

    if (window.locationHistory && !interactionIsAnAssessmentQuestion) {
      const locationHistory = window.locationHistory;

      window.location.hash = locationHistory;

      const checkHistory = setInterval(() => {
        if (locationHistory !== window.locationHistory) {
          clearInterval(checkHistory);

          requestAnimationFrame(
            () =>
              (window.location.hash = Math.random()
                .toString(16)
                .substr(2, 8))
          );

          (window as any).locationHistory = null;
        }
      }, 1000 / 60);

      return;
    }
    //

    if (isIE()) {
      const section = document.querySelector('section')!;
      const { top } = section.getBoundingClientRect();
      const bounds = window.innerHeight * 0.6 * -1;
      if (top === 0) {
        navToPrevTopic();
        return;
      }
    }

    const { sections, index } = getSectionInView();

    const prevSection = sections[index - 1];

    if (prevSection) {
      prevSection.scrollIntoView({
        behavior: 'smooth',
      });

      onScrollEnds(() => {
        prevSection.focus();
      });
    } else {
      navToPrevTopic();
    }
  };

  return (
    <a
      ref={node => {
        if (!backBtn.current) backBtn.current = node;
      }}
      id="chameleon-back-button"
      // href="#go-back"
      title="Return to previous interaction"
      css={css`
        position: relative;
        display: flex;
        justify-content: center;
        align-items: center;
        flex-shrink: 0;
        width: 30px;
        height: 30px;
        border-radius: 50%;
        transform: scaleX(-1);
        transition: opacity 0.2s;
        cursor: pointer;
        -webkit-backface-visibility: hidden;
        transition: all 0.2s;

        &:hover {
          background-color: ${lGrey2};
        }

        &:focus {
          ${getButtonFocus(color)}
        }

        ${outline &&
          `
        border: 1px solid ${color};

        &:hover {
          background-color: rgba(255, 255, 255, 0.1);
        }

        `}
      `}
      onClick={onClick}
    >
      <VisuallyHidden>Go Back</VisuallyHidden>
      <Arrow color={color} />
    </a>
  );
};

export default BackBtn;
